"use client";

import { c as _c } from "react/compiler-runtime";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ModelSelector } from "@/components/model-selector";
import { McpServersDialog } from "@/components/playground/mcp-servers-dialog";
import { ShareChatDialog } from "@/components/playground/share-chat-dialog";
import { TempChatSwitcher } from "@/components/playground/temp-chat-switcher";
import { Label } from "@/components/ui/label";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useI18n } from "@llmgateway/i18n/react";
import type { McpServer } from "@/hooks/useMcpServers";
import type { ApiModel, ApiProvider } from "@/lib/fetch-models";
import type { Organization } from "@/lib/types";
interface ChatHeaderProps {
  models: ApiModel[];
  providers: ApiProvider[];
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  comparisonEnabled: boolean;
  onComparisonEnabledChange: (enabled: boolean) => void;
  hideCompare?: boolean;
  showGlobalModelSelector: boolean;
  // MCP servers props
  mcpServers: McpServer[];
  onAddMcpServer: (server: Omit<McpServer, "id">) => McpServer;
  onUpdateMcpServer: (id: string, updates: Partial<Omit<McpServer, "id">>) => void;
  onRemoveMcpServer: (id: string) => void;
  onToggleMcpServer: (id: string) => void;
  isTemporaryChat: boolean;
  onToggleTemporaryChat: () => void;
  showTemporaryChatSwitcher: boolean;
  isTemporaryChatToggleDisabled: boolean;
  hasTemporaryMessages: boolean;
  currentChatId: string | null;
  isShareChatDisabled?: boolean;
  shareId: string | null;
  orgShares: Array<{
    id: string;
    organizationId: string;
  }>;
  organizations: Organization[];
  chatTitle?: string | null;
  previewPrompt?: string | null;
}
export const ChatHeader = t0 => {
  const $ = _c(49);
  const {
    models,
    providers,
    selectedModel,
    setSelectedModel,
    comparisonEnabled,
    onComparisonEnabledChange,
    hideCompare: t1,
    showGlobalModelSelector,
    mcpServers,
    onAddMcpServer,
    onUpdateMcpServer,
    onRemoveMcpServer,
    onToggleMcpServer,
    isTemporaryChat,
    onToggleTemporaryChat,
    showTemporaryChatSwitcher,
    isTemporaryChatToggleDisabled,
    hasTemporaryMessages,
    currentChatId,
    isShareChatDisabled: t2,
    shareId,
    orgShares,
    organizations,
    chatTitle,
    previewPrompt
  } = t0;
  const hideCompare = t1 === undefined ? false : t1;
  const isShareChatDisabled = t2 === undefined ? true : t2;
  const {
    t
  } = useI18n();
  let t3;
  if ($[0] !== isTemporaryChat) {
    t3 = isTemporaryChat ? null : <SidebarTrigger />;
    $[0] = isTemporaryChat;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== models || $[3] !== providers || $[4] !== selectedModel || $[5] !== setSelectedModel || $[6] !== showGlobalModelSelector || $[7] !== t) {
    t4 = showGlobalModelSelector ? <div className="flex w-full min-w-0 max-w-[360px] items-center gap-2 sm:max-w-[420px]"><ModelSelector models={models} providers={providers} value={selectedModel} onValueChange={setSelectedModel} placeholder={t("modelSelector.searchSelectModel")} /></div> : null;
    $[2] = models;
    $[3] = providers;
    $[4] = selectedModel;
    $[5] = setSelectedModel;
    $[6] = showGlobalModelSelector;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex min-w-0 flex-1 items-center gap-3">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LanguageSwitcher className="hidden sm:flex" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== hasTemporaryMessages || $[14] !== isTemporaryChat || $[15] !== isTemporaryChatToggleDisabled || $[16] !== onToggleTemporaryChat || $[17] !== showTemporaryChatSwitcher) {
    t7 = showTemporaryChatSwitcher ? <TempChatSwitcher isTemporaryChat={isTemporaryChat} onToggleTemporaryChat={onToggleTemporaryChat} isTemporaryChatToggleDisabled={isTemporaryChatToggleDisabled} hasTemporaryMessages={hasTemporaryMessages} /> : null;
    $[13] = hasTemporaryMessages;
    $[14] = isTemporaryChat;
    $[15] = isTemporaryChatToggleDisabled;
    $[16] = onToggleTemporaryChat;
    $[17] = showTemporaryChatSwitcher;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== chatTitle || $[20] !== comparisonEnabled || $[21] !== currentChatId || $[22] !== isShareChatDisabled || $[23] !== isTemporaryChat || $[24] !== orgShares || $[25] !== organizations || $[26] !== previewPrompt || $[27] !== shareId) {
    t8 = isTemporaryChat || !currentChatId || comparisonEnabled ? null : <ShareChatDialog currentChatId={currentChatId} disabled={isShareChatDisabled} shareId={shareId} orgShares={orgShares} organizations={organizations} chatTitle={chatTitle} previewPrompt={previewPrompt} />;
    $[19] = chatTitle;
    $[20] = comparisonEnabled;
    $[21] = currentChatId;
    $[22] = isShareChatDisabled;
    $[23] = isTemporaryChat;
    $[24] = orgShares;
    $[25] = organizations;
    $[26] = previewPrompt;
    $[27] = shareId;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== mcpServers || $[30] !== onAddMcpServer || $[31] !== onRemoveMcpServer || $[32] !== onToggleMcpServer || $[33] !== onUpdateMcpServer) {
    t9 = <TooltipProvider><McpServersDialog servers={mcpServers} onAddServer={onAddMcpServer} onUpdateServer={onUpdateMcpServer} onRemoveServer={onRemoveMcpServer} onToggleServer={onToggleMcpServer} /></TooltipProvider>;
    $[29] = mcpServers;
    $[30] = onAddMcpServer;
    $[31] = onRemoveMcpServer;
    $[32] = onToggleMcpServer;
    $[33] = onUpdateMcpServer;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== comparisonEnabled || $[36] !== hideCompare || $[37] !== isTemporaryChat || $[38] !== onComparisonEnabledChange || $[39] !== t) {
    t10 = isTemporaryChat ? null : !hideCompare || comparisonEnabled ? <div className="hidden items-center gap-2 md:flex">{hideCompare ? <span className="text-muted-foreground text-xs">{t("playground.comparisonMode")}</span> : <><Label htmlFor="comparison-mode" className="text-muted-foreground text-xs">{t("playground.comparisonMode")}</Label><Switch id="comparison-mode" checked={comparisonEnabled} onCheckedChange={onComparisonEnabledChange} /></>}</div> : null;
    $[35] = comparisonEnabled;
    $[36] = hideCompare;
    $[37] = isTemporaryChat;
    $[38] = onComparisonEnabledChange;
    $[39] = t;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t11 = <div className="ml-3 flex items-center gap-3">{t6}{t7}{t8}{t9}{t10}</div>;
    $[41] = t10;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t11 || $[47] !== t5) {
    t12 = <header className="bg-background flex items-center border-b p-4">{t5}{t11}</header>;
    $[46] = t11;
    $[47] = t5;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
};